/* TH Series — shared scroll-reveal helper.
 * Adds .is-visible to any .m-th-fadeup or .m-th-reveal element when it enters the viewport.
 * Idempotent: safe to load multiple times. */
(function () {
  if (window.__thSeriesReveal) return;
  window.__thSeriesReveal = true;

  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var supportsIO = 'IntersectionObserver' in window;
  var io = null;

  function getIO() {
    if (io || reducedMotion || !supportsIO) return io;
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    return io;
  }

  // Reveal any not-yet-shown .m-th-fadeup / .m-th-reveal within `scope` (default document).
  function reveal(scope) {
    var root = (scope && scope.querySelectorAll) ? scope : document;
    var nodes = root.querySelectorAll('.m-th-fadeup, .m-th-reveal');
    if (!nodes.length) return;

    // Honor reduced motion (or no IO) — render the finished state, skip animation.
    if (reducedMotion || !supportsIO) {
      nodes.forEach(function (n) { n.classList.add('is-visible'); });
      return;
    }

    var obs = getIO();
    var viewportH = window.innerHeight || document.documentElement.clientHeight;
    nodes.forEach(function (n) {
      if (n.classList.contains('is-visible')) return;
      // Elements already in the viewport reveal immediately (no flash of invisible
      // content); below-fold ones get the scroll reveal.
      var rect = n.getBoundingClientRect();
      if (rect.top < viewportH * 0.85 && rect.bottom > 0) {
        n.classList.add('is-visible');
      } else {
        obs.observe(n);
      }
    });
  }

  function init() { reveal(document); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor / Section Rendering API: an added or re-rendered section fires
  // shopify:section:load (NOT DOMContentLoaded). Without re-running the reveal here,
  // the re-rendered section's .m-th-fadeup elements stay at opacity:0 — the section
  // looks blank until a full page reload. rAF lets the injected DOM lay out first.
  document.addEventListener('shopify:section:load', function (e) {
    requestAnimationFrame(function () { reveal(e.target || document); });
  });
})();

/* ── ScrollTrigger position hardening (site-wide) ────────────────────────────
   The 2026 sections create once:true reveal triggers as soon as GSAP lands,
   but long pages keep growing AFTER that: lazy images, videos, carousels,
   metafield media. Every late layout shift leaves trigger starts hundreds of
   px below the sections they guard, so content sits hidden (autoAlpha 0) long
   after it is on screen. Re-sync trigger positions — debounced, and only when
   the document height actually moved — whenever late layout lands. A refresh
   makes past-start once:true triggers fire immediately, un-sticking reveals. */
(function () {
  var pending = null;
  var lastH = 0;
  function refresh() {
    pending = null;
    if (!window.ScrollTrigger) return;
    var h = document.documentElement.scrollHeight;
    if (Math.abs(h - lastH) < 4) return; // nothing moved — skip the forced layout
    lastH = h;
    window.ScrollTrigger.refresh();
  }
  function queue() {
    if (pending) return;
    pending = setTimeout(refresh, 250);
  }
  // Lazy media anywhere in the page ('load' doesn't bubble — capture phase).
  document.addEventListener('load', function (e) {
    var t = e.target;
    if (t && (t.tagName === 'IMG' || t.tagName === 'VIDEO')) queue();
  }, true);
  // Fonts settling changes SplitText line geometry and heading heights.
  if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
    document.fonts.ready.then(queue);
  }
  // Anything else that changes document height (carousels, embeds, app widgets).
  function watchBody() {
    lastH = document.documentElement.scrollHeight;
    if ('ResizeObserver' in window && document.body) {
      new ResizeObserver(queue).observe(document.body);
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchBody, { once: true });
  } else {
    watchBody();
  }
  window.addEventListener('load', queue);
})();

/* ── Reveal-fire guard ────────────────────────────────────────────────────────
   Long pages mutate layout constantly (lazy media, tabs, carousels, third-party
   widgets), and a ScrollTrigger position captured during a transient collapse
   can leave once:true reveals firing thousands of px before their element is
   on screen ("everything already visible"). Positions can lie; live geometry
   cannot: wrap ScrollTrigger.create/batch so a once-reveal that fires while
   its element is still far below the fold is swallowed and re-armed with an
   IntersectionObserver, which fires at TRUE viewport entry. */
(function () {
  function wrap(ST) {
    if (ST.__epoGuard) return;
    ST.__epoGuard = true;
    function farBelow(node) {
      if (!node || !node.getBoundingClientRect) return false;
      var vh = window.innerHeight || document.documentElement.clientHeight || 0;
      return node.getBoundingClientRect().top > vh * 1.15;
    }
    function rearm(node, fire) {
      /* Live-geometry re-arm. Not an IntersectionObserver: an instant jump
         (jump-nav, anchor) can move the element from below to above the
         viewport between frames, and IO never observes an intersection.
         top < 95% viewport covers both entering AND already-passed. */
      var done = false, ticking = false;
      function check() {
        if (done) return;
        var vh = window.innerHeight || document.documentElement.clientHeight || 0;
        if (node.getBoundingClientRect().top < vh * 0.95) {
          done = true;
          window.removeEventListener('scroll', onScroll);
          fire();
        }
      }
      function onScroll() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () { ticking = false; check(); });
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      check();
    }
    var origCreate = ST.create;
    ST.create = function (vars) {
      if (vars && vars.once && typeof vars.onEnter === 'function' && vars.trigger && !vars.scrub && !vars.pin) {
        var onEnter = vars.onEnter;
        vars.onEnter = function (self) {
          var node = self && self.trigger;
          if (farBelow(node)) {
            var st = this;
            rearm(node, function () { onEnter.call(st, self); });
            if (self && self.kill) self.kill();
            return;
          }
          return onEnter.apply(this, arguments);
        };
      }
      return origCreate.call(this, vars);
    };
    var origBatch = ST.batch;
    ST.batch = function (targets, vars) {
      if (vars && vars.once && typeof vars.onEnter === 'function') {
        var onEnter = vars.onEnter;
        vars.onEnter = function (elements, triggers) {
          var near = [], ctx = this;
          (elements || []).forEach(function (el) {
            if (farBelow(el)) rearm(el, function () { onEnter.call(ctx, [el], triggers); });
            else near.push(el);
          });
          if (near.length) return onEnter.call(ctx, near, triggers);
        };
      }
      return origBatch.call(this, targets, vars);
    };
  }
  /* Wrap the instant the library lands — a timer poll can lose the race
     against section scripts that build on the DOMContentLoaded microtask. */
  if (window.ScrollTrigger) { wrap(window.ScrollTrigger); }
  else {
    try {
      var _st;
      Object.defineProperty(window, 'ScrollTrigger', {
        configurable: true, enumerable: true,
        get: function () { return _st; },
        set: function (v) { _st = v; if (v && v.create) { try { wrap(v); } catch (e) {} } }
      });
    } catch (e) {
      (function poll(n) {
        if (window.ScrollTrigger) return wrap(window.ScrollTrigger);
        if (n <= 0) return;
        setTimeout(function () { poll(n - 1); }, 25);
      })(400);
    }
  }
})();
